import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UtilService } from '../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '../../services/error.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { Pagination } from '../../services/api/api';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { SizeService } from '../../services/size-detection/size.service';
import { MsOptions, MultiSelectInterface } from '../../services/global/global-interfaces';
import { takeUntil } from 'rxjs/operators';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';
import { SbIconName } from '@ngx-seatback/icons';

@Component({
  selector: 'sb-comp-dropdown-select',
  templateUrl: './sb-dropdown-select.component.html',
  styleUrls: ['./sb-dropdown-select.component.scss'],
  standalone: false,
})
export class SbDropdownSelectComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  @Input() tooltip: string;
  @Input() showText: boolean = true;
  @Input() showIcon: boolean = true;
  @Input() showColumnIcon: boolean = true;
  @Input() showSubmit: boolean = true;
  @Input() dropDownDatatype: string;
  @Input() saveUserSettings: boolean = true;
  @Input() showChevronDown: boolean = true;
  @Input() singleSelectMode: boolean = false;
  @Input() disabled: boolean = false;
  @Input() useNoSelectionLabel: boolean = false;
  @Input() frontEndSearch: boolean = false;
  @Input() hidePagination: boolean = false;

  @Output() selectedDataEmitter: EventEmitter<any[]>;
  @Output() msOptionsChange: EventEmitter<MsOptions> = new EventEmitter<MsOptions>();

  @ViewChild('dropdown') dropdown: NgbDropdown;
  dropDownData: MultiSelectInterface[];
  paginationInitialized: boolean;
  maxSize: number;
  paginationString: string;
  boundaryLinks: boolean;
  collectionSize: number;
  resultCount: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  selectedData: any[];
  submitted: boolean;
  showModalLike: boolean;
  private modalOption: boolean;
  private readonly destroy$ = new Subject();

  constructor(
    public util: UtilService,
    public translate: TranslateService,
    public errorHandler: ErrorService,
    public sizeService: SizeService
  ) {
    this.selectedDataEmitter = new EventEmitter<any[]>();
    this.selectedData = [];
    this.dropDownData = [];

    this._page = 1;
    this.paginationInitialized = false;
    this.maxSize = 8;
    this.paginationString = '';
    this.boundaryLinks = true;
    this.collectionSize = -1;
    this.resultCount = -1;
    this.submitted = false;
    this.showModalLike = false;
    this.modalOption = false;
    this._msOptions.pagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: this.maxSize ? this.maxSize : 10,
      pages: -1,
      resultcount: -1,
    });
  }

  _msOptions: MsOptions = {
    search: null,
    pagination: null,
  };

  get msOptions() {
    return this._msOptions;
  }

  @Input() set msOptions(val) {
    if (val) {
      if (val.pagination) {
        this._msOptions.pagination = val.pagination;
      }
      if (val.search) {
        this._msOptions.search = val.search;
      }
    }
  }

  // tslint:disable-next-line:variable-name
  _page: number;

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.msOptions.pagination.value.page = val;
  }

  // tslint:disable-next-line:variable-name
  _inputData: any[];

  @Input() set inputData(values: any[]) {
    this._inputData = [];
    this._inputData = values;
    if (this.msOptions.pagination) {
      this.paginationInitialized = true;
    }
    this.paginationString = this.stringTransformation.getPaginationString(
      this.page,
      this.maxSize,
      this._inputData?.length,
      this.resultCount,
      this.dropDownDatatype ? this.stringTransformation.toCamelCase(this.dropDownDatatype) : null
    );
    this.destroySub();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.paginationString = this.stringTransformation.getPaginationString(
        this.page,
        this.maxSize,
        this._inputData?.length,
        this.resultCount,
        this.dropDownDatatype ? this.stringTransformation.toCamelCase(this.dropDownDatatype) : null
      );
    });
    this.updateDropdown();
  }

  // tslint:disable-next-line:variable-name
  _selectedInputData: any[]; // Location, company, ...

  @Input() set selectedInputData(values: any[]) {
    this._selectedInputData = [];
    this._selectedInputData = values;
    this.selectedData = [];
    this.updateDropdown();
  }

  ngOnInit(): void {
    this.modalOption = this.sizeService.checkScreensize(['md', 'sm', 'xs']);
    this.sizeService.onResize$().subscribe(() => {
      if (this.dropdown?._open) {
        this.dropdown.close();
      }
    });
    this.initPagination().then(() => {
      this.requestDebounced();
    });
  }

  destroySub() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  updateDropdown() {
    this.dropDownData = (this._inputData || []).map(element => ({
      id: element.id,
      name: this.frontEndSearch ? element : element.name,
      identifier: element.identifier,
      selected: false,
      data: element,
    }));
    if (this._selectedInputData && this._selectedInputData.length > 0) {
      for (const element of this._selectedInputData) {
        const foundItem = this.dropDownData.find(item => item.id === element.id);
        if (foundItem) {
          foundItem.selected = true;
        }
        if (!this.selectedData.some(item => item.id === element.id)) {
          this.selectedData.push(element);
        }
      }
    }
  }

  initPagination(): Promise<void> {
    return new Promise(res => {
      this.msOptions.pagination.subscribe((e: Pagination) => {
        this.collectionSize = e.resultcount;
        this.maxSize = e.count;
        this.resultCount = e.resultcount;
      });
      res();
    });
  }

  pageChange($event) {
    this.page = $event;
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      // options push
      if (this.msOptions?.pagination) {
        this.msOptionsChange.emit(this.msOptions);
      }
    }, 500);
  }

  deselectAll() {
    for (const column of this.dropDownData) {
      column.selected = false;
    }
    this.selectedData = [];
    if (!this.showSubmit) {
      this.prepareOutput();
    }
    if (this.singleSelectMode) {
      if (!this.modalOption) {
        this.dropdown.close();
      } else {
        this.toggleModalDropdownSelect();
      }
      this.prepareOutput();
    }
  }

  sortedData() {
    return this.sortData(this.dropDownData);
  }

  filterData(searchStr: string) {
    let permissions: MultiSelectInterface[];
    if (searchStr) {
      const ret: MultiSelectInterface[] = [];
      for (const dataElement of this.dropDownData) {
        let translated: string = '';
        if (this.dropDownDatatype === 'PERMISSIONS') {
          translated = this.translate.instant('permission.' + dataElement.name);
        }
        if (
          dataElement.name === searchStr ||
          translated.toLowerCase() === searchStr.toLowerCase() ||
          dataElement.name.toLowerCase().includes(searchStr.toLowerCase()) ||
          translated.toLowerCase().includes(searchStr.toLowerCase())
        ) {
          ret.push(dataElement);
        }
      }
      permissions = ret;
    } else {
      permissions = this.dropDownData;
    }
    return this.sortData(permissions);
  }

  sortData(unsorted: any[]) {
    return [...unsorted].sort((a, b) => {
      const aValue = a.name ?? a.id?.toString() ?? '';
      const bValue = b.name ?? b.id?.toString() ?? '';
      return aValue.localeCompare(bValue);
    });
  }

  checkColumn($event, data) {
    $event.stopPropagation();
    $event.preventDefault();

    if (!this.singleSelectMode) {
      if (!this.checkIfSelected(data)) {
        data.selected = true;
        this.selectedData.push(data.data);
      } else {
        data.selected = false;
        this.selectedData = this.selectedData.filter(item => item.id !== data.id);
      }

      if (!this.showSubmit) {
        this.prepareOutput();
      }
    } else {
      if (this.selectedData.length === 0) {
        data.selected = true;
        this.selectedData.push(data.data);
      } else if (this.selectedData.length >= 1) {
        if (!this.checkIfSelected(data)) {
          for (const column of this.dropDownData) {
            column.selected = false;
          }
          this.selectedData = [];
          data.selected = true;
          this.selectedData.push(data.data);
        } else {
          data.selected = false;
          this.selectedData.splice(0, 1);
        }
      }

      if (!this.modalOption) {
        this.dropdown.close();
      } else {
        this.toggleModalDropdownSelect();
      }
      this.prepareOutput();
    }
  }

  checkIfSelected(data: MultiSelectInterface): boolean {
    return this.frontEndSearch ? this.selectedData.includes(data.data) : this.selectedData.some(selectedCol => selectedCol.id === data.id);
  }

  submit() {
    this.submitted = true;
    if (!this.modalOption) {
      this.dropdown.close();
    } else {
      this.toggleModalDropdownSelect();
    }
    this.prepareOutput();
  }

  closeCall(event) {
    if (!this.singleSelectMode) {
      if (!event && !this.submitted) {
        this.selectedData = [];
        this.updateDropdown();
      }
      if (this.submitted) {
        this.submitted = false;
      }
    }
  }

  prepareOutput() {
    const output: any[] = [];
    if (this.selectedData && this.selectedData.length > 0) {
      for (const singleElement of this.selectedData) {
        if (singleElement) {
          output.push(singleElement);
        }
      }
    }
    this.selectedDataEmitter.emit(output);
  }

  getColumnIcon(): SbIconName {
    switch (this.dropDownDatatype) {
      case 'COMPANIES':
        return 'company';
      case 'DEVICES':
        return 'playerOnline';
      case 'LOCATIONS':
        return 'location';
      case 'POOLS':
        return 'pools';
      case 'CHANNELS':
        return 'stele';
      case 'MANAGED':
        return 'company';
      case 'AREA_CATEGORIES':
        return 'locationWorld';
      case 'SSP_CONTRACTS':
        return 'contract';
      case 'CAMPAIGN_TYPES':
        return 'megaphoneTypes';
      case 'TEMPLATE_GROUPS':
        return 'templates';
      case 'MEDIUM_TRANSFORMATION':
        return 'expand';
      case 'PRODUCT_CATEGORIES':
        return 'productCategory';
      case 'PLAYER_HARDWARE':
        return 'playerSetting';
      case 'SCREEN_HARDWARE':
        return 'screenSetting';
      case 'PLAYER_SOFTWARE':
        return 'playerSoftware';
      case 'PLAYER_CULLS':
        return 'playerOnline';
      case 'GEO_CULLS':
        return 'locationStreet';
      case 'GEO_CULLS2':
        return 'globe';
      case 'TIME_CULLS':
        return 'calendar';
      case 'CLIENT_CULLS':
        return 'companyClient';
      case 'CONTENT_CULLS':
        return 'screenPlay';
      case 'NOTIFICATION_CATEGORIES':
        return 'filter';
      case 'ROLES':
      case 'ADMINROLES':
        return 'userRoles';
      case 'PERMISSIONS':
        return 'key';
      case 'REPORTING_TYPES':
        return 'categoryPieChart';
      case 'REPORTING_TEMPLATES':
        return 'templatePieChart';
      case 'TIME_RANGES':
        return 'lineMarkerArrow';
      case 'CRON_CREATION':
      case 'CRON_END':
        return 'crons';
      case 'PLANNINGS':
        return 'plannings';
      case 'ASSET_CATEGORIES':
        return 'category';
      case 'CULL_CATEGORIES':
        return 'category';
      case 'MEDIUMGROUP_CATEGORIES':
        return 'category';
      case 'TAGS':
        return 'tags';
      case 'DEVICE_LOG_DATATYPES':
        return 'list';
      case 'REPOSITORIES':
        return 'folderPhoto';
      case 'ADVERTISERS':
        return 'companyClient';
      case 'WEEKLY_SCHEDULES':
        return 'clock';
      case 'MEDIA':
        return 'motive';
      case 'BOOKINGS':
        return 'bookings';
      default:
        return 'settings';
    }
  }

  getButtonText(): string {
    // show "no selection" in button when nothing is selected and useNoSelectionLabel is enabled
    if (this.useNoSelectionLabel && this.selectedData?.length === 0) {
      return this.translate.instant('lang.globals.noSelection');
    }

    if (this.selectedData?.length === 1) {
      return this.getDisplayedName(this.selectedData[0]);
    }

    switch (this.dropDownDatatype) {
      case 'LOCATIONS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.locations');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.locations');
        }
        break;
      case 'COMPANIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.companies');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.companies');
        }
        break;
      case 'POOLS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.pools');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.pools');
        }
        break;
      case 'DEVICES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.devices');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.devices');
        }
        break;
      case 'CHANNELS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.channels');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.channels');
        }
        break;
      case 'MANAGED':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.attributeToggles.company.managesLocations');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.attributeToggles.company.managesLocations');
        }
        break;
      case 'AREA_CATEGORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.areaCategories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.areaCategories');
        }
        break;
      case 'MEDIUM_TRANSFORMATION':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.noSelection');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.mediumTransformation');
        }
        break;
      case 'SSP_CONTRACTS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.sspContracts');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.sspContracts');
        }
        break;
      case 'CAMPAIGN_TYPES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.campaignTypes');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.campaignTypes');
        }
        break;
      case 'TEMPLATE_GROUPS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.templateGroups');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.templateGroups');
        }
        break;
      case 'PRODUCT_CATEGORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.productCategories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.productCategories');
        }
        break;
      case 'PLAYER_HARDWARE':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.noSelection');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.playerHardware');
        }
        break;
      case 'SCREEN_HARDWARE':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.noSelection');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.screenHardware');
        }
        break;
      case 'PLAYER_SOFTWARE':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.noSelection');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.playerSoftware');
        }
        break;
      case 'PLAYER_CULLS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.playerFilter');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.playerFilter');
        }
        break;
      case 'GEO_CULLS':
      case 'GEO_CULLS2':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.geoFilter');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.geoFilter');
        }
        break;
      case 'TIME_CULLS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.timeFilter');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.timeFilter');
        }
        break;
      case 'CONTENT_CULLS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.contentFilter');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.contentFilter');
        }
        break;
      case 'CLIENT_CULLS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.clientFilter');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.clientFilter');
        }
        break;
      case 'NOTIFICATION_CATEGORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.notificationCategories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.notificationCategories');
        }
        break;
      case 'ROLES':
      case 'ADMINROLES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.roles');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.roles');
        }
        break;
      case 'PERMISSIONS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.permissions');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.permissions');
        }
        break;
      case 'REPORTING_TYPES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.reporting.reportingType');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.reporting.reportingType');
        }
        break;
      case 'REPORTING_TEMPLATES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.reporting.reportingTemplate');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.reporting.reportingTemplate');
        }
        break;
      case 'CRON_CREATION':
      case 'CRON_END':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.reporting.cronDefinition');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.reporting.cronDefinition');
        }
        break;
      case 'TIME_RANGES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.reporting.timeRange');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.reporting.timeRange');
        }
        break;
      case 'PLANNINGS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.planningsList.headline');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.planningsList.headline');
        }
        break;
      case 'ASSET_CATEGORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.assetCategories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.assetCategories');
        }
        break;
      case 'CULL_CATEGORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.cullCategories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.cullCategories');
        }
        break;
      case 'MEDIUMGROUP_CATEGORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.mediumGroupCategories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.mediumGroupCategories');
        }
        break;
      case 'DEVICE_LOG_DATATYPES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.deviceDetails.logDatatypes');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.deviceDetails.logDatatypes');
        }
        break;
      case 'REPOSITORIES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.repositories');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.repositories');
        }
        break;
      case 'ADVERTISERS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.advertisers');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.advertisers');
        }
        break;
      case 'WEEKLY_SCHEDULES':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.openingHours');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.openingHours');
        }
        break;
      case 'MEDIA':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.medias');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.medias');
        }
        break;
      case 'BOOKINGS':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.bookings');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.bookings');
        }
        break;
      case 'TAGS':
        if (this.selectedData?.length === 0) {
          return 'Tags';
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + 'Tags';
        }
        break;

      default:
        return 'Unknown Datatype';
    }
  }

  getHeadline(): string {
    switch (this.dropDownDatatype) {
      case 'LOCATIONS':
        return this.translate.instant('lang.globals.locations');
      case 'COMPANIES':
        return this.translate.instant('lang.globals.companies');
      case 'POOLS':
        return this.translate.instant('lang.globals.pools');
      case 'DEVICES':
        return this.translate.instant('lang.globals.devices');
      case 'CHANNELS':
        return this.translate.instant('lang.globals.channels');
      case 'MANAGED':
        return this.translate.instant('lang.attributeToggles.company.managesLocations');
      case 'AREA_CATEGORIES':
        return this.translate.instant('lang.globals.areaCategories');
      case 'SSP_CONTRACTS':
        return this.translate.instant('lang.globals.sspContracts');
      case 'MEDIUM_TRANSFORMATION':
        return this.translate.instant('lang.globals.mediumTransformation');
      case 'CAMPAIGN_TYPES':
        return this.translate.instant('lang.globals.campaignTypes');
      case 'TEMPLATE_GROUPS':
        return this.translate.instant('lang.globals.templateGroups');
      case 'PRODUCT_CATEGORIES':
        return this.translate.instant('lang.globals.productCategories');
      case 'PLAYER_HARDWARE':
        return this.translate.instant('lang.globals.playerHardware');
      case 'SCREEN_HARDWARE':
        return this.translate.instant('lang.globals.screenHardware');
      case 'PLAYER_SOFTWARE':
        return this.translate.instant('lang.globals.playerSoftware');
      case 'PLAYER_CULLS':
        return this.translate.instant('lang.globals.playerFilter');
      case 'GEO_CULLS':
      case 'GEO_CULLS2':
        return this.translate.instant('lang.globals.geoFilter');
      case 'TIME_CULLS':
        return this.translate.instant('lang.globals.timeFilter');
      case 'CLIENT_CULLS':
        return this.translate.instant('lang.globals.clientFilter');
      case 'CONTENT_CULLS':
        return this.translate.instant('lang.globals.contentFilter');
      case 'NOTIFICATION_CATEGORIES':
        return this.translate.instant('lang.globals.notificationCategories');
      case 'ROLES':
      case 'ADMINROLES':
        return this.translate.instant('lang.globals.roles');
      case 'PERMISSIONS':
        return this.translate.instant('lang.globals.permissions');
      case 'REPORTING_TYPES':
        return this.translate.instant('lang.reporting.reportingType');
      case 'REPORTING_TEMPLATES':
        return this.translate.instant('lang.reporting.reportingTemplate');
      case 'CRON_CREATION':
      case 'CRON_END':
        return this.translate.instant('lang.reporting.cronDefinition');
      case 'TIME_RANGES':
        return this.translate.instant('lang.reporting.timeRange');
      case 'PLANNINGS':
        return this.translate.instant('lang.planningsList.headline');
      case 'ASSET_CATEGORIES':
        return this.translate.instant('lang.globals.assetCategories');
      case 'CULL_CATEGORIES':
        return this.translate.instant('lang.globals.cullCategories');
      case 'MEDIUMGROUP_CATEGORIES':
        return this.translate.instant('lang.globals.mediumGroupCategories');
      case 'DEVICE_LOG_DATATYPES':
        return this.translate.instant('lang.deviceDetails.logDatatypes');
      case 'REPOSITORIES':
        return this.translate.instant('lang.globals.repositories');
      case 'ADVERTISERS':
        return this.translate.instant('lang.globals.advertisers');
      case 'WEEKLY_SCHEDULES':
        return this.translate.instant('lang.globals.openingHours');
      case 'MEDIA':
        return this.translate.instant('lang.globals.medias');
      case 'BOOKINGS':
        return this.translate.instant('lang.globals.bookings');
      case 'TAGS':
        return 'Tags';
      default:
        return 'Unknown Datatype';
    }
  }

  toggleModalDropdownSelect() {
    this.showModalLike = !this.showModalLike;
  }

  getButtonWidth(): string {
    let width: number = 42;
    width += this.showIcon ? 20 : 0;
    const headlineLength = (this.getHeadline().length + 2) * 9;
    const length: number = headlineLength < 180 ? headlineLength : 180;
    width += this.showText ? length : 0;
    width += this.showChevronDown ? 10 : 0;
    return width + 'px';
  }

  getDisplayedName(obj: any): string {
    if (this.frontEndSearch) {
      if (this.dropDownDatatype === 'PERMISSIONS') {
        return this.translate.instant('permission.' + (obj?.data ?? obj.toString()));
      }
    }
    if (this.dropDownDatatype === 'ROLES') {
      return this.translate.instant(obj.name);
    }
    return obj?.name?.toString() ?? obj?.identifier?.toString() ?? obj?.id?.toString() ?? '';
  }
}
